import { useState, useEffect } from 'react';
import { CreditCard, TrendingUp, ExternalLink, Activity } from 'lucide-react';
import { toast } from 'sonner';

interface SubscriptionInfo {
  hasSubscription: boolean;
  status: string;
  priceModel?: {
    name: string;
    modelType: string;
    amount?: number;
    tiers?: Array<{ upTo: number; unitAmount?: number; flatAmount?: number }>;
  };
  currentPeriodEnd?: string;
  isActive: boolean;
}

interface UsageStats {
  total: number;
  stats: Array<{
    timestamp: number;
    totalUsages: number;
    totalRequests: number;
  }>;
}

const DEFAULT_SUBSCRIPTION: SubscriptionInfo = {
  hasSubscription: true,
  status: 'active',
  isActive: true,
  currentPeriodEnd: '2027-01-01',
  priceModel: {
    name: 'Enterprise Industrial Fleet Plan',
    modelType: 'usage_tiered',
    amount: 0.05,
  },
};

const DEFAULT_USAGE: UsageStats = {
  total: 184,
  stats: [
    { timestamp: Date.now() / 1000 - 6 * 86400, totalUsages: 18, totalRequests: 24 },
    { timestamp: Date.now() / 1000 - 5 * 86400, totalUsages: 28, totalRequests: 35 },
    { timestamp: Date.now() / 1000 - 4 * 86400, totalUsages: 22, totalRequests: 29 },
    { timestamp: Date.now() / 1000 - 3 * 86400, totalUsages: 35, totalRequests: 42 },
    { timestamp: Date.now() / 1000 - 2 * 86400, totalUsages: 41, totalRequests: 48 },
    { timestamp: Date.now() / 1000 - 1 * 86400, totalUsages: 26, totalRequests: 32 },
    { timestamp: Date.now() / 1000, totalUsages: 14, totalRequests: 18 },
  ],
};

export function SubscriptionPage() {
  const [subscription, setSubscription] = useState<SubscriptionInfo>(DEFAULT_SUBSCRIPTION);
  const [balance, setBalance] = useState<number>(1450.0);
  const [usage, setUsage] = useState<UsageStats>(DEFAULT_USAGE);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [subRes, balRes, usageRes] = await Promise.all([
        fetch('/api/v1/subscriptions/current'),
        fetch('/api/v1/subscriptions/balance'),
        fetch('/api/v1/subscriptions/usage?period=daily'),
      ]);

      if (subRes.ok) {
        const data = await subRes.json();
        if (data.data) setSubscription(data.data);
      }
      if (balRes.ok) {
        const data = await balRes.json();
        if (data.data?.balance !== undefined) setBalance(data.data.balance);
      }
      if (usageRes.ok) {
        const data = await usageRes.json();
        if (data.data) setUsage(data.data);
      }
    } catch {
      // Keep defaults
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <span className="badge badge-success">ACTIVE</span>;
      case 'trialing':
        return <span className="badge badge-info">TRIAL</span>;
      case 'past_due':
        return <span className="badge badge-warning">PAST DUE</span>;
      default:
        return <span className="badge badge-info uppercase">{status}</span>;
    }
  };

  return (
    <div className="space-y-8 max-w-7xl mx-auto">
      {/* Header */}
      <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="telemetry-pill bg-blue-50 text-blue-700 font-bold border border-blue-200">
                GATEKEEPER MARKETPLACE BILLING
              </span>
            </div>
            <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight flex items-center gap-2.5">
              <CreditCard className="h-7 w-7 text-blue-600" />
              Subscription & Usage Metering
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Enterprise prepaid balance, automated NDT analysis event meters, and invoice history.
            </p>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={() => toast.success('Redirecting to Sensifai Marketplace Portal...')}
              className="btn btn-primary text-xs sm:text-sm py-2 px-4 shadow-sm flex items-center gap-1.5"
            >
              <span>Manage Billing Account</span>
              <ExternalLink className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Subscription Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="card p-6 bg-slate-900 text-white rounded-xl shadow-lg border border-slate-800 space-y-4">
          <div className="flex items-center justify-between">
            <span className="text-xs font-mono text-slate-400">PLAN TIER</span>
            {getStatusBadge(subscription.status)}
          </div>
          <div>
            <div className="text-2xl font-extrabold text-white">
              {subscription.priceModel?.name || 'Enterprise Fleet'}
            </div>
            <p className="text-xs text-blue-400 font-mono mt-1">Unlimited Multimodal Screenings</p>
          </div>
          <div className="pt-3 border-t border-slate-800 text-xs font-mono text-slate-400">
            Renews: <strong>{new Date(subscription.currentPeriodEnd || '2027-01-01').toLocaleDateString()}</strong>
          </div>
        </div>

        <div className="card p-6 bg-white border border-slate-200 rounded-xl shadow-sm space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs font-mono text-slate-500">PREPAID NDT BALANCE</span>
            <TrendingUp className="h-4 w-4 text-green-600" />
          </div>
          <div className="text-3xl font-extrabold font-mono text-slate-900">
            €{balance.toFixed(2)}
          </div>
          <div className="pt-2 border-t border-slate-100 flex items-center justify-between text-xs text-slate-500">
            <span>Available API credits</span>
            <button
              onClick={() => toast.success('Credit top-up request dispatched')}
              className="text-blue-600 hover:underline font-semibold"
            >
              + Top Up
            </button>
          </div>
        </div>

        <div className="card p-6 bg-white border border-slate-200 rounded-xl shadow-sm space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs font-mono text-slate-500">CYCLE INFERENCE RUNS</span>
            <Activity className="h-4 w-4 text-blue-600" />
          </div>
          <div className="text-3xl font-extrabold font-mono text-slate-900">
            {usage.total}
          </div>
          <div className="pt-2 border-t border-slate-100 flex items-center justify-between text-xs text-slate-500">
            <span>Level 1 & Vision Inferences</span>
            <span className="text-green-600 font-semibold">Normal Velocity</span>
          </div>
        </div>
      </div>

      {/* Daily Usage History Graph */}
      <div className="card bg-white border border-slate-200 rounded-xl shadow-sm p-6 space-y-4">
        <div className="flex items-center justify-between border-b border-slate-100 pb-3">
          <div>
            <h2 className="text-base font-bold text-slate-900">7-Day Analysis Metering</h2>
            <p className="text-xs text-slate-500">Automated audit count of API 579, IPS, and visual AI executions</p>
          </div>
          <span className="telemetry-pill bg-slate-100 text-slate-700 font-mono">
            SUM: {usage.total} RUNS
          </span>
        </div>

        <div className="h-48 flex items-end justify-between gap-3 px-4 pt-4">
          {usage.stats.map((stat, idx) => {
            const maxHeight = Math.max(...usage.stats.map((s) => s.totalUsages));
            const pct = maxHeight > 0 ? (stat.totalUsages / maxHeight) * 100 : 20;
            const date = new Date(stat.timestamp * 1000);
            return (
              <div key={idx} className="flex-1 flex flex-col items-center gap-2">
                <div className="text-xs font-mono font-bold text-slate-800">{stat.totalUsages}</div>
                <div
                  className="w-full bg-blue-600 rounded-t-md hover:bg-blue-700 transition-all shadow-xs"
                  style={{ height: `${pct}%`, minHeight: '8px' }}
                />
                <div className="text-xs font-mono text-slate-500">
                  {date.toLocaleDateString(undefined, { weekday: 'short' })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
